"use client";
import React, { useState, useMemo } from "react";
import { motion } from "motion/react";
import { Search, Grid3x3, List, Layers } from "lucide-react";
import { GlassCard } from "@/src/components/GlassCard";
import { Button } from "@/src/components/ui/button";
import Link from "next/link";
import {
  useMarketingDiecutCategories,
  useMarketingDiecutsPage,
} from "@/src/hooks/useMarketingDiecuts";
import { useDebouncedValue } from "@/src/hooks/useDebouncedValue";
import { MARKETING_DIECUTS_PAGE_SIZE } from "@/src/constants/marketing-diecuts";
import { DiecutCard } from "./DiecutCard";
import { DiecutCardSkeleton } from "./DiecutCardSkeleton";
import { useTranslation } from "react-i18next";
import { DiecutPagination } from "./DiecutPagination";


export const DieCutLibraryPage: React.FC = () => {
  const { t } = useTranslation("common");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(searchQuery, 300).trim();

  const { data: categorySummary = [] } = useMarketingDiecutCategories();
  const { data, isLoading } = useMarketingDiecutsPage({
    page,
    category: selectedCategory === "all" ? undefined : selectedCategory,
    search: debouncedSearch || undefined,
  });

  const dieCuts = data?.items ?? [];
  const filteredTotal = data?.total ?? 0;
  const totalPages = Math.max(
    1,
    Math.ceil(filteredTotal / MARKETING_DIECUTS_PAGE_SIZE),
  );

  const categories = useMemo(
    () => [
      {
        id: "all",
        name: t("marketingPages.diecuts.allCategory"),
        count: categorySummary.reduce((sum, c) => sum + c.count, 0),
      },
      ...categorySummary
        .filter((c) => c.count > 0)
        .map((c) => ({ id: c.slug, name: c.name, count: c.count })),
    ],
    [categorySummary, t],
  );

  const handleCategoryChange = (id: string) => {
    setSelectedCategory(id);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  return (
    <div className="min-h-screen pt-32 pb-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 backdrop-blur-[5px] bg-white/10 dark:bg-white/5 px-6 py-3 rounded-[33px] border border-white/20 dark:border-white/10 mb-6 glass-shadow-outer relative">
            <div
              aria-hidden="true"
              className="absolute inset-0 pointer-events-none rounded-[inherit] glass-shadow-inset"
            />
            <Layers className="w-5 h-5 text-walnut-600 dark:text-walnut-400" />
            <span className="text-sm font-medium text-grey-800 dark:text-grey-200 relative z-10">
              {t("marketingPages.diecuts.badge")}
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl font-bold text-walnut-900 dark:text-white mb-4">
            {t("marketingPages.diecuts.title")}
          </h1>
          <p className="text-lg text-walnut-700 dark:text-walnut-300 max-w-2xl mx-auto">
            {t("marketingPages.diecuts.subtitle")}
          </p>
        </motion.div>

        {/* Search and View Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <GlassCard className="p-6">
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              <div className="flex-1 w-full lg:max-w-md relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-walnut-600 dark:text-walnut-400" />
                <input
                  type="text"
                  placeholder={t("marketingPages.diecuts.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-[20px] backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 text-walnut-900 dark:text-white placeholder-walnut-600 dark:placeholder-walnut-400 focus:outline-none focus:border-walnut-500 transition-all"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setViewMode("grid")}
                  variant="glass"
                  size="icon"
                  className={`rounded-[20px] ${
                    viewMode === "grid"
                      ? "bg-walnut-500/20 border-walnut-500/30 text-walnut-700 dark:text-walnut-300"
                      : "text-walnut-600 dark:text-walnut-400"
                  }`}
                >
                  <Grid3x3 className="w-5 h-5" />
                </Button>
                <Button
                  onClick={() => setViewMode("list")}
                  variant="glass"
                  size="icon"
                  className={`rounded-[20px] ${
                    viewMode === "list"
                      ? "bg-walnut-500/20 border-walnut-500/30 text-walnut-700 dark:text-walnut-300"
                      : "text-walnut-600 dark:text-walnut-400"
                  }`}
                >
                  <List className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </GlassCard>
        </motion.div>

        {/* Categories */}
        {!isLoading && categories.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <div className="flex flex-wrap gap-3">
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  variant={selectedCategory === cat.id ? "gradient" : "glass"}
                  pill
                >
                  {cat.name}
                  <span className="ml-2 text-sm opacity-80">({cat.count})</span>
                </Button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Results Count */}
        {!isLoading && (
          <div className="mb-6 text-walnut-700 dark:text-walnut-300">
            {t("marketingPages.diecuts.results", {
              filtered: filteredTotal,
              total: categories[0].count,
            })}
          </div>
        )}

        {/* Die-Cut Grid/List */}
        <div
          className={
            viewMode === "grid"
              ? "grid md:grid-cols-2 lg:grid-cols-3 gap-6"
              : "space-y-4"
          }
        >
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <DiecutCardSkeleton key={i} viewMode={viewMode} />
              ))
            : dieCuts.map((dieCut, index) => (
                <motion.div
                  key={dieCut.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <DiecutCard dieCut={dieCut} viewMode={viewMode} />
                </motion.div>
              ))}
        </div>

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <DiecutPagination
            page={page}
            totalPages={totalPages}
            onPageChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        )}

        {/* Empty State */}
        {!isLoading && dieCuts.length === 0 && (
          <div className="text-center py-20">
            <GlassCard className="p-12 max-w-md mx-auto">
              <div className="w-16 h-16 rounded-2xl bg-walnut-100 dark:bg-walnut-800 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-walnut-600 dark:text-walnut-400" />
              </div>
              <h3 className="text-xl font-bold text-walnut-900 dark:text-white mb-2">
                {t("marketingPages.diecuts.emptyTitle")}
              </h3>
              <p className="text-walnut-700 dark:text-walnut-300">
                {t("marketingPages.diecuts.emptySubtitle")}
              </p>
            </GlassCard>
          </div>
        )}

        {/* CTA Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <GlassCard className="p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-walnut-900 dark:text-white mb-4">
              {t("marketingPages.diecuts.ctaTitle")}
            </h2>
            <p className="text-lg text-walnut-700 dark:text-walnut-300 mb-6 max-w-2xl mx-auto">
              {t("marketingPages.diecuts.ctaSubtitle")}
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-[33px] bg-gradient-to-r from-walnut-600 to-walnut-700 dark:from-walnut-500 dark:to-walnut-600 text-white font-semibold hover:shadow-lg hover:shadow-walnut-500/30 transition-all duration-300 hover:scale-105"
            >
              {t("marketingPages.diecuts.ctaButton")}
            </Link>
          </GlassCard>
        </motion.div>
      </div>
    </div>
  );
};
